import { ActiveFilterPills } from "@/components/marketplace/active-filter-pills";
import { EmptyStateMessage } from "@/components/marketplace/empty-state-message";
import { MultiSelectCombobox } from "@/components/marketplace/multi-select-combobox";
import { captureEvent } from "@/lib/analytics-loader";
import { proxyImageUrl } from "@/lib/image-url";
import {
  MARKETPLACE_PAGE_SIZE,
  useMarketplaceCampaignsQuery,
  useMarketplaceCampaignsV2Query,
  useMarketplaceOwnProgramIdsQuery,
} from "@/lib/queries/marketplace";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  Input,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Skeleton,
  Switch,
} from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  Filter,
  Instagram,
  Linkedin,
  Loader2,
  Youtube,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OrgRouteContext } from "./route";

// ... existing imports ...

// ... inside Marketplace component ...
// Track search queries
// ... existing imports ...

// ... inside CampaignCard component ...
// CampaignCard moved below due to dependency on cardContent

export const Route = createFileRoute("/(org)/o/$orgSlug/marketplace")({
  component: Marketplace,
  validateSearch: (search) =>
    ({
      legacySearch:
        search.legacySearch === "true" ||
        search.legacySearch === true ||
        undefined,
    }) as { legacySearch?: boolean },
});

// ============================================================================
// Types
// ============================================================================

export interface MarketplaceFilterOption {
  value: string;
  label: string;
  isSelected: boolean;
}

export interface MarketplaceFilterGroup {
  isAllSelected: boolean;
  options: MarketplaceFilterOption[];
}

export interface TMarketplaceFilters {
  compensation: MarketplaceFilterGroup;
  network: MarketplaceFilterGroup;
  category: MarketplaceFilterGroup;
  /** Hide campaigns the selected creator has applied to (EPD-12258). Client-side only. */
  hideApplied: boolean;
}

type TMarketplaceFilterGroupKey = "compensation" | "network" | "category";

/** One removable pill for an active Marketplace filter (EPD-12259). */
export type TMarketplaceFilterPill =
  | {
      key: string;
      label: string;
      group: TMarketplaceFilterGroupKey;
      value: string;
    }
  | { key: string; label: string; group: "hideApplied" };

// ============================================================================
// Initial Filter State
// ============================================================================

export const initMarketplaceFilters: TMarketplaceFilters = {
  compensation: {
    isAllSelected: true,
    options: [
      { value: "offers_payment", label: "Payment", isSelected: false },
      { value: "offers_product", label: "Product", isSelected: false },
      { value: "offers_commission", label: "Commission", isSelected: false },
      { value: "offers_other", label: "Other", isSelected: false },
    ],
  },
  network: {
    isAllSelected: true,
    options: [
      { value: "instagram", label: "Instagram", isSelected: false },
      { value: "tiktok", label: "TikTok", isSelected: false },
      { value: "youtube", label: "YouTube", isSelected: false },
      { value: "pinterest", label: "Pinterest", isSelected: false },
      { value: "video", label: "Video", isSelected: false },
      { value: "image", label: "Image", isSelected: false },
      { value: "custom_work", label: "Custom Work", isSelected: false },
    ],
  },
  category: {
    isAllSelected: true,
    options: [
      { value: "selfhelp", label: "Advice/Education", isSelected: false },
      { value: "beauty", label: "Beauty", isSelected: false },
      { value: "fashion", label: "Fashion", isSelected: false },
      { value: "fitness", label: "Fitness", isSelected: false },
      { value: "food", label: "Food/Drink", isSelected: false },
      { value: "wellness", label: "Health/Wellness", isSelected: false },
      { value: "decor", label: "Home/Design", isSelected: false },
      { value: "kidsfamily", label: "Kids/Family", isSelected: false },
      { value: "pets", label: "Pets", isSelected: false },
      { value: "tech", label: "Tech", isSelected: false },
      { value: "travel", label: "Travel", isSelected: false },
      { value: "other", label: "Other", isSelected: false },
    ],
  },
  hideApplied: true,
};

// Simple icon component type that accepts className
type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * TikTok icon component (lucide-react doesn't have TikTok icon)
 */
const TikTok: IconComponent = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

/**
 * Pinterest icon component (lucide-react doesn't have Pinterest icon)
 */
const Pinterest: IconComponent = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49-.09-.79-.17-2.01.03-2.87.18-.78 1.16-4.97 1.16-4.97s-.3-.6-.3-1.49c0-1.38.8-2.41 1.8-2.41.85 0 1.26.64 1.26 1.4 0 .85-.54 2.13-.82 3.31-.23.98.49 1.78 1.46 1.78 1.75 0 3.1-1.85 3.1-4.52 0-2.36-1.7-4.01-4.13-4.01-2.81 0-4.46 2.11-4.46 4.29 0 .85.33 1.76.74 2.3.08.1.09.19.07.29-.07.3-.24.94-.27 1.07-.04.17-.14.21-.33.13-1.24-.58-2.02-2.4-2.02-3.86 0-3.15 2.29-6.05 6.61-6.05 3.47 0 6.17 2.47 6.17 5.77 0 3.45-2.17 6.22-5.18 6.22-1.01 0-1.97-.53-2.3-1.23l-.63 2.4c-.23.89-.85 2.01-1.27 2.69.96.3 1.98.45 3.01.45 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
);

/**
 * Video icon — Fresh Icons solid VideoIcon, inlined for visual parity
 * with the aspirex Content Guideline Template UI (EPD-7469).
 */
const VideoIcon: IconComponent = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 576 512"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M384 112v288c0 26.51-21.49 48-48 48H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h288c26.5 0 48 21.49 48 48zm192 15.5v256.9c0 25.5-29.17 40.39-50.39 25.79L416 334.7V177.3l109.6-75.56c21.3-14.61 50.4.36 50.4 25.76z" />
  </svg>
);

/**
 * Image icon — Fresh Icons solid ImageIcon, inlined for visual parity
 * with the aspirex Content Guideline Template UI (EPD-7469). Named
 * `ImageIcon` (not `Image`) to avoid shadowing the DOM `Image` global.
 */
const ImageIcon: IconComponent = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 512 512"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M447.1 32h-384C28.64 32-.01 60.65-.01 96v320c0 35.35 28.65 64 63.1 64h384c35.35 0 64-28.65 64-64V96c.01-35.35-27.79-64-63.99-64zm-336 64c26.51 0 48 21.49 48 48s-20.6 48-48 48-48-21.49-48-48 22.38-48 48-48zm335 311.6c-2.8 5.2-8.2 8.4-14.1 8.4H82.01a15.993 15.993 0 0 1-14.26-8.75 16 16 0 0 1 1.334-16.68l70-96C142.1 290.4 146.9 288 152 288s9.916 2.441 12.93 6.574l32.46 44.51 93.3-139.1C293.7 194.7 298.7 192 304 192s10.35 2.672 13.31 7.125l128 192c3.29 4.875 3.59 11.175.79 16.475z" />
  </svg>
);

/**
 * Custom Work icon — Fresh Icons solid ScrewdriverWrenchIcon, inlined
 * for visual parity with the aspirex Content Guideline Template UI (EPD-7469).
 */
const CustomWorkIcon: IconComponent = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 512 512"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M331.8 224.1c28.29 0 54.88 10.99 74.86 30.97l19.59 19.59c40.01-17.74 71.25-53.3 81.62-96.65 5.725-23.92 5.34-47.08.215-68.4-2.613-10.88-16.43-14.51-24.34-6.604l-68.9 68.9h-75.6V97.2l68.9-68.9c7.912-7.912 4.275-21.73-6.604-24.34-21.32-5.125-44.48-5.51-68.4.215-55.3 13.23-98.39 60.22-107.2 116.4C224.5 128.9 224.2 137 224.3 145l82.78 82.86c8.12-2.76 16.42-3.76 24.72-3.76zm52.2 54.5c-23.16-23.16-57.57-27.57-85.39-13.9L191.1 158V95.99L64 0 0 63.1l96 127.1 62.04.008 106.7 106.6c-13.67 27.82-9.251 62.23 13.91 85.39l117 117.1c14.62 14.5 38.21 14.5 52.71-.002l52.75-52.75c14.5-14.5 14.5-38.08-.002-52.71L384 278.6zM227.9 307l-59.2-59.1L19.8 396.8c-26.37 26.37-26.37 69.08 0 95.45C32.96 505.4 50.21 512 67.5 512s34.54-6.592 47.72-19.78l119.1-119.1C225.5 352.3 222.6 329.4 227.9 307zM64 472c-13.25 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.7 24 24-10.75 24-24 24z" />
  </svg>
);

export const socialIcons: Record<string, IconComponent> = {
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: TikTok,
  pinterest: Pinterest,
  video: VideoIcon,
  image: ImageIcon,
  custom_work: CustomWorkIcon,
};

/**
 * Normalize network type from API to social media platform name used in UI.
 */
export function normalizeNetworkType(networkType: string): string {
  const normalized = networkType.toLowerCase();
  // Map common variations to our icon keys
  if (normalized.includes("instagram")) return "instagram";
  if (normalized.includes("youtube")) return "youtube";
  if (normalized.includes("linkedin")) return "linkedin";
  if (normalized.includes("tiktok")) return "tiktok";
  if (normalized.includes("pinterest")) return "pinterest";
  // Legacy ContentPostTypes values come through as "CUSTOM_WORK" / "IMAGES" /
  // "VIDEOS"; lowercase above, then collapse singular/plural to our keys.
  if (normalized.includes("custom_work") || normalized === "customwork") {
    return "custom_work";
  }
  if (normalized === "video" || normalized === "videos") return "video";
  if (normalized === "image" || normalized === "images") return "image";
  return normalized;
}

/**
 * Transform API campaign to UI campaign format.
 */
function transformCampaign(campaign: {
  id: number;
  campaignId: string;
  programId: number | null;
  name: string;
  brandName: string;
  brandLogoUrl: string | null;
  summary: string | null;
  splashImageUrl: string | null;
  networkTypes: string[];
  categories: string[];
  offersPayment: boolean;
  offersProduct: boolean;
  offersCommission: boolean;
  offersOther: boolean;
  externalListingUrl: string | null;
}) {
  return {
    id: campaign.campaignId || String(campaign.id),
    programId: campaign.programId,
    title: campaign.name,
    description: campaign.summary || "",
    imageUrl: campaign.splashImageUrl || "",
    brandLogo: campaign.brandLogoUrl || "",
    brandName: campaign.brandName,
    socialMedia: campaign.networkTypes.map(normalizeNetworkType),
    networkTypes: campaign.networkTypes,
    categories: campaign.categories,
    offersPayment: campaign.offersPayment,
    offersProduct: campaign.offersProduct,
    offersCommission: campaign.offersCommission,
    offersOther: campaign.offersOther,
    externalUrl: campaign.externalListingUrl,
  };
}

export function Marketplace() {
  const context = Route.useRouteContext() as OrgRouteContext;
  const { organization } = context;
  const { legacySearch } = Route.useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Track search queries
  useEffect(() => {
    if (debouncedSearchQuery.trim() && organization?.id) {
      captureEvent("marketplace_searched", {
        organizationId: organization.id,
        query: debouncedSearchQuery,
      });
    }
  }, [debouncedSearchQuery, organization?.id]);

  // Applied filters - used for the actual query
  const [appliedFilters, setAppliedFilters] = useState<TMarketplaceFilters>(
    initMarketplaceFilters,
  );
  // Pending filters - what's shown in the filter UI (not applied until button is clicked)
  const [pendingFilters, setPendingFilters] = useState<TMarketplaceFilters>(
    initMarketplaceFilters,
  );

  // When filter sheet opens, sync pending filters with applied filters
  const handleFilterOpen = (open: boolean) => {
    setIsFilterOpen(open);
    if (open) {
      // When opening, initialize pending filters from applied filters
      setPendingFilters(appliedFilters);
    }
  };

  // Map applied filter state to API format
  const apiFilters = useMemo(() => {
    const filter: {
      networks?: string[];
      categories?: string[];
      compensations?: string[];
      keywords?: string[];
    } = {};

    // Get selected networks
    const selectedNetworks = appliedFilters.network.options
      .filter((opt) => opt.isSelected)
      .map((opt) => opt.value);
    if (selectedNetworks.length > 0) filter.networks = selectedNetworks;

    // Get selected categories
    const selectedCategories = appliedFilters.category.options
      .filter((opt) => opt.isSelected)
      .map((opt) => opt.value);
    if (selectedCategories.length > 0) filter.categories = selectedCategories;

    // Get selected compensations — pass values directly to API
    // The legacy API expects: "offers_payment", "offers_product", "offers_commission", "offers_other"
    // which match the filter option values exactly
    const selectedCompensations = appliedFilters.compensation.options
      .filter((opt) => opt.isSelected)
      .map((opt) => opt.value);
    if (selectedCompensations.length > 0)
      filter.compensations = selectedCompensations;

    // Add search keywords (search applies immediately, not via Apply Filters)
    // Use debounced search query to avoid too many API calls
    if (debouncedSearchQuery.trim()) {
      // Split search query into keywords by spaces, filter out empty strings
      const keywords = debouncedSearchQuery
        .trim()
        .split(/\s+/)
        .filter((keyword) => keyword.length > 0);
      if (keywords.length > 0) {
        filter.keywords = keywords;
      }
    }

    return filter;
  }, [appliedFilters, debouncedSearchQuery]);

  // Fetch filtered campaigns with infinite scroll — both hooks called
  // unconditionally to satisfy Rules of Hooks; `enabled` controls which fires.
  const v1Result = useMarketplaceCampaignsQuery(
    organization?.id ?? "",
    apiFilters,
    !!legacySearch,
  );
  const v2Result = useMarketplaceCampaignsV2Query(
    organization?.id ?? "",
    apiFilters,
    !legacySearch,
  );
  const {
    data,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = legacySearch ? v1Result : v2Result;

  const campaigns = useMemo(
    () =>
      data?.pages.flatMap((page) => page.campaigns.map(transformCampaign)) ??
      [],
    [data],
  );

  // Programs the selected creator (org) applied to, and programs a brand added
  // them to directly. A failed lookup leaves both sets empty, so the
  // Marketplace still renders, without badges.
  const ownProgramIdsQuery = useMarketplaceOwnProgramIdsQuery(
    organization?.id ?? "",
  );
  const appliedProgramIds = useMemo(
    () => new Set(ownProgramIdsQuery.data?.appliedProgramIds ?? []),
    [ownProgramIdsQuery.data],
  );
  const directlyAddedProgramIds = useMemo(
    () => new Set(ownProgramIdsQuery.data?.directlyAddedProgramIds ?? []),
    [ownProgramIdsQuery.data],
  );

  // Campaigns the creator was added to directly are always hidden, so they
  // can't apply to a program they're already in (EPD-12258). That isn't a
  // filter, so it doesn't change the empty-state copy.
  const availableCampaigns = useMemo(
    () => withoutDirectlyAdded(campaigns, directlyAddedProgramIds),
    [campaigns, directlyAddedProgramIds],
  );
  const visibleCampaigns = useMemo(
    () =>
      getVisibleCampaigns(
        availableCampaigns,
        appliedFilters.hideApplied,
        appliedProgramIds,
      ),
    [availableCampaigns, appliedFilters.hideApplied, appliedProgramIds],
  );
  const hiddenCampaignCount =
    availableCampaigns.length - visibleCampaigns.length;

  // Sentinel for infinite scroll. A callback ref, so the observer attaches
  // whenever the sentinel mounts (it can mount after the first render).
  const [isSentinelVisible, setIsSentinelVisible] = useState(false);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement) => {
    sentinelNodeRef.current = node;
    const observer = new IntersectionObserver(
      (entries) => setIsSentinelVisible(entries[0]?.isIntersecting ?? false),
      { rootMargin: `${SENTINEL_MARGIN_PX}px` },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      sentinelNodeRef.current = null;
      setIsSentinelVisible(false);
    };
  }, []);

  // Pages arrive 24 at a time and hidden campaigns can leave one short or
  // empty, so keep loading while the sentinel stays on screen (the observer
  // won't fire again if a page added nothing visible) or less than a page is
  // visible. The server reports hasMore from its own page, so this ends.
  useEffect(() => {
    if (
      shouldFetchNextPage({
        hasNextPage,
        hasError: error != null,
        isFetchingNextPage,
        // Re-measure: right after a page renders, the observer hasn't yet
        // reported that the new cards pushed the sentinel off screen.
        isSentinelVisible:
          isSentinelVisible && isNearViewport(sentinelNodeRef.current),
        visibleCount: visibleCampaigns.length,
      })
    ) {
      fetchNextPage();
    }
  }, [
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isSentinelVisible,
    visibleCampaigns.length,
  ]);

  // Helper to get selected values for MultiSelect (from pending filters)
  const getSelectedValues = (group: TMarketplaceFilterGroupKey): string[] => {
    return pendingFilters[group].options
      .filter((opt) => opt.isSelected)
      .map((opt) => opt.value);
  };

  // Helper to handle MultiSelect selection change (updates pending filters)
  const handleFilterChange = (
    group: TMarketplaceFilterGroupKey,
    selected: string[],
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        options: prev[group].options.map((opt) => ({
          ...opt,
          isSelected: selected.includes(opt.value),
        })),
        isAllSelected: selected.length === 0,
      },
    }));
  };

  const handleHideAppliedChange = (checked: boolean) => {
    setPendingFilters((prev) => ({ ...prev, hideApplied: checked }));
  };

  // Convert filter groups to MultiSelect options format (from pending filters)
  const filterOptions = useMemo(() => {
    return {
      networks: pendingFilters.network.options.map((opt) => ({
        label: opt.label,
        value: opt.value,
      })),
      categories: pendingFilters.category.options.map((opt) => ({
        label: opt.label,
        value: opt.value,
      })),
      compensations: pendingFilters.compensation.options.map((opt) => ({
        label: opt.label,
        value: opt.value,
      })),
    };
  }, [pendingFilters]);

  // Apply pending filters to applied filters
  const handleApplyFilters = () => {
    setAppliedFilters(pendingFilters);
    setIsFilterOpen(false);
  };

  // Clear all filters (both pending and applied). Unlike a fresh visit,
  // Clear All turns "Hide campaigns I've applied to" off (EPD-12258).
  const handleClearAll = () => {
    const clearedFilters = { ...initMarketplaceFilters, hideApplied: false };
    setPendingFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
  };

  const activeFilterPills = useMemo(
    () => getActiveFilterPills(appliedFilters),
    [appliedFilters],
  );

  // A pill's × applies at once, no Apply click, and the panel follows (EPD-12259).
  const handleRemoveFilterPill = (pill: TMarketplaceFilterPill) => {
    const nextFilters = removeFilterPill(appliedFilters, pill);
    setPendingFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Marketplace</h1>
        <p className="text-muted-foreground mt-2">
          Discover new campaign opportunities
        </p>
      </div>

      {/* Search and Filter Bar, with the active filter pills below it */}
      <div>
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Input
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleFilterOpen(true)}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>
        <ActiveFilterPills
          pills={activeFilterPills}
          onRemove={handleRemoveFilterPill}
          onClearAll={handleClearAll}
        />
      </div>

      {/* Wait for the creator's programs too, so badges and hidden cards never change late. */}
      {isPending || ownProgramIdsQuery.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load campaigns. Please try again.
          </AlertDescription>
        </Alert>
      ) : visibleCampaigns.length === 0 && !hasNextPage ? (
        <Card>
          <CardContent className="py-12 text-center">
            <EmptyStateMessage
              emptyReason={data?.pages[0]?.emptyReason}
              orgSlug={organization?.slug ?? ""}
              hasActiveFilters={
                !appliedFilters.compensation.isAllSelected ||
                !appliedFilters.network.isAllSelected ||
                !appliedFilters.category.isAllSelected ||
                // The toggle is on by default; it only counts once it hid something.
                (appliedFilters.hideApplied && hiddenCampaignCount > 0) ||
                debouncedSearchQuery.trim().length > 0
              }
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {visibleCampaigns.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  isApplied={isCampaignApplied(
                    campaign.programId,
                    appliedProgramIds,
                  )}
                />
              ))}
            </div>
          )}

          {/* Loading spinner for next page, or while skipping hidden pages */}
          {(isFetchingNextPage || visibleCampaigns.length === 0) && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Sentinel for IntersectionObserver */}
          {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
        </>
      )}

      {/* Filter Dialog / Drawer */}
      <ResponsiveDialog open={isFilterOpen} onOpenChange={handleFilterOpen}>
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Filter Campaigns</ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <div className="space-y-6">
            <MultiSelectCombobox
              label="Network"
              options={filterOptions.networks}
              selected={getSelectedValues("network")}
              onSelectionChange={(selected) =>
                handleFilterChange("network", selected)
              }
              placeholder="Select networks..."
            />
            <MultiSelectCombobox
              label="Category"
              options={filterOptions.categories}
              selected={getSelectedValues("category")}
              onSelectionChange={(selected) =>
                handleFilterChange("category", selected)
              }
              placeholder="Select categories..."
            />
            <MultiSelectCombobox
              label="Compensation"
              options={filterOptions.compensations}
              selected={getSelectedValues("compensation")}
              onSelectionChange={(selected) =>
                handleFilterChange("compensation", selected)
              }
              placeholder="Select compensation types..."
            />
            <div className="flex items-center justify-between gap-4 rounded-[10px] border border-border px-4 py-3.5">
              <Label
                htmlFor="marketplace-hide-applied"
                className="text-[15px] font-medium text-foreground"
              >
                Hide campaigns I've applied to
              </Label>
              <Switch
                id="marketplace-hide-applied"
                checked={pendingFilters.hideApplied}
                onCheckedChange={handleHideAppliedChange}
                className="h-[22px] w-10 duration-150 data-[state=checked]:bg-chart-1 data-[state=unchecked]:bg-border [&>span]:size-[18px] [&>span]:bg-white [&>span]:duration-150 [&>span]:data-[state=checked]:translate-x-[18px]"
              />
            </div>
          </div>
          <ResponsiveDialogFooter className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClearAll}
            >
              Clear All
            </Button>
            <Button className="flex-1" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  );
}
interface CampaignCardProps {
  campaign: {
    id: string;
    programId: number | null;
    title: string;
    description: string;
    imageUrl: string;
    brandLogo: string;
    brandName: string;
    socialMedia: string[];
    networkTypes: string[];
    categories: string[];
    offersPayment: boolean;
    offersProduct: boolean;
    offersCommission: boolean;
    offersOther: boolean;
    externalUrl: string | null;
  };
  /** The selected creator has applied to this campaign (EPD-12237). */
  isApplied?: boolean;
}

/**
 * Whether a campaign's program is in the applied set. Listings with no
 * program (affiliate recruit) are never applied.
 */
export function isCampaignApplied(
  programId: number | null,
  appliedProgramIds: ReadonlySet<number>,
): boolean {
  return programId !== null && appliedProgramIds.has(programId);
}

/**
 * Campaigns minus those whose program a brand added the creator to directly
 * (EPD-12258): the creator is already in them, so they're never shown.
 */
export function withoutDirectlyAdded<T extends { programId: number | null }>(
  campaigns: T[],
  directlyAddedProgramIds: ReadonlySet<number>,
): T[] {
  if (directlyAddedProgramIds.size === 0) return campaigns;
  return campaigns.filter(
    (campaign) =>
      campaign.programId === null ||
      !directlyAddedProgramIds.has(campaign.programId),
  );
}

/** The campaigns to show: all of them, or only unapplied ones (EPD-12258). */
export function getVisibleCampaigns<T extends { programId: number | null }>(
  campaigns: T[],
  hideApplied: boolean,
  appliedProgramIds: ReadonlySet<number>,
): T[] {
  if (!hideApplied || appliedProgramIds.size === 0) return campaigns;
  return campaigns.filter(
    (campaign) => !isCampaignApplied(campaign.programId, appliedProgramIds),
  );
}

/** How far outside the viewport the infinite-scroll sentinel starts loading. */
const SENTINEL_MARGIN_PX = 200;

/** Whether `node` is within `margin` px of the viewport, measured now. */
export function isNearViewport(
  node: Element | null,
  margin = SENTINEL_MARGIN_PX,
): boolean {
  if (!node) return false;
  const { top, bottom } = node.getBoundingClientRect();
  return top < window.innerHeight + margin && bottom > -margin;
}

/**
 * Whether to load the next page without waiting for the user to scroll: the
 * sentinel is still on screen, or hiding applied campaigns left less than a
 * page visible (EPD-12258).
 */
export function shouldFetchNextPage({
  hasNextPage,
  hasError,
  isFetchingNextPage,
  isSentinelVisible,
  visibleCount,
}: {
  hasNextPage: boolean;
  /** A failed fetch keeps hasNextPage true, so stop rather than retry in a loop. */
  hasError: boolean;
  isFetchingNextPage: boolean;
  isSentinelVisible: boolean;
  visibleCount: number;
}): boolean {
  if (!hasNextPage || hasError || isFetchingNextPage) return false;
  return isSentinelVisible || visibleCount < MARKETPLACE_PAGE_SIZE;
}

const FILTER_PILL_GROUP_ORDER: TMarketplaceFilterGroupKey[] = [
  "network",
  "category",
  "compensation",
];

/**
 * One pill per applied filter value: networks, categories, compensation types,
 * then "Excluding applied" while the hide-applied toggle is on (EPD-12259).
 */
export function getActiveFilterPills(
  filters: TMarketplaceFilters,
): TMarketplaceFilterPill[] {
  const pills: TMarketplaceFilterPill[] = FILTER_PILL_GROUP_ORDER.flatMap(
    (group) =>
      filters[group].options
        .filter((opt) => opt.isSelected)
        .map((opt) => ({
          key: `${group}-${opt.value}`,
          label: opt.label,
          group,
          value: opt.value,
        })),
  );
  if (filters.hideApplied) {
    pills.push({
      key: "hideApplied",
      label: "Excluding applied",
      group: "hideApplied",
    });
  }
  return pills;
}

/** The filters with one pill's value removed (EPD-12259). */
export function removeFilterPill(
  filters: TMarketplaceFilters,
  pill: TMarketplaceFilterPill,
): TMarketplaceFilters {
  if (pill.group === "hideApplied") return { ...filters, hideApplied: false };
  const options = filters[pill.group].options.map((opt) =>
    opt.value === pill.value ? { ...opt, isSelected: false } : opt,
  );
  return {
    ...filters,
    // No value left in the group means "all", as in the filter panel.
    [pill.group]: {
      options,
      isAllSelected: !options.some((opt) => opt.isSelected),
    },
  };
}

export function CampaignCard({
  campaign,
  isApplied = false,
}: CampaignCardProps) {
  const { organization } = Route.useRouteContext() as OrgRouteContext;

  const handleCampaignClick = () => {
    captureEvent("campaign_clicked", {
      organizationId: organization?.id,
      campaignId: campaign.id,
      campaignTitle: campaign.title,
      external: !!campaign.externalUrl,
    });
  };

  const imageContent = (
    // `isolate` keeps the badge's z-index inside the card, so it stays above
    // the image but below the sticky page header.
    <div className="relative isolate w-full aspect-4/3 overflow-hidden bg-muted">
      {isApplied && (
        <Badge
          variant="success"
          className="absolute top-3 left-3 z-10 shadow-[0_1px_2px_0_rgb(0_0_0/0.15)]"
        >
          Applied
        </Badge>
      )}
      {campaign.imageUrl ? (
        <img
          src={proxyImageUrl(campaign.imageUrl)}
          alt={campaign.title}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-muted" aria-hidden="true" />
      )}
    </div>
  );

  const cardContent = (
    <>
      {/* Main Image - takes up most of the space */}
      {imageContent}

      {/* Footer */}
      <CardFooter className="flex items-start gap-3 p-4 pt-3 shrink-0">
        {/* Brand Logo - Left */}
        <div className="shrink-0">
          {campaign.brandLogo ? (
            <img
              src={proxyImageUrl(campaign.brandLogo)}
              alt={campaign.brandName}
              className="w-10 h-10 rounded-full object-cover border"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-full bg-muted border"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Campaign Info + Social Icons */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm leading-tight mb-1 truncate">
            {campaign.brandName}
          </h3>
          <p className="text-xs line-clamp-2">{campaign.title}</p>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {campaign.description}
          </p>
          {/* Social Media Icons */}
          <div className="flex items-center gap-1.5 mt-2">
            {campaign.socialMedia.map((platform) => {
              const Icon = socialIcons[platform as keyof typeof socialIcons];
              if (!Icon) return null;
              return (
                <div
                  key={platform}
                  className="w-6 h-6 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/10 transition-colors"
                >
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        </div>
      </CardFooter>
    </>
  );

  return (
    <Card
      className="overflow-hidden flex flex-col hover:shadow-lg transition-shadow cursor-pointer"
      onClick={handleCampaignClick}
    >
      {campaign.externalUrl ? (
        <a
          href={campaign.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col flex-1"
          onClick={(e) => {
            // Allow default behavior for link but still track
            e.stopPropagation();
            handleCampaignClick();
          }}
        >
          {cardContent}
        </a>
      ) : (
        cardContent
      )}
    </Card>
  );
}
